import { OneFlowError } from '../core/errors';
import type { Engine } from './engine';
import { createObjectUrl, revokeObjectUrl } from './object-urls';

interface SaveWritable {
  write(data: BufferSource): Promise<void>;
  close(): Promise<void>;
  abort?(): Promise<void>;
}
interface SaveHandle {
  createWritable(): Promise<SaveWritable>;
}
type SavePicker = (opts: { suggestedName: string }) => Promise<SaveHandle>;

export type ExportMode = 'picker' | 'download-link';

const CHUNK = 4 * 1024 * 1024;

/** Which export path this browser can use. ALG-051, ALG-052 */
export function exportMode(): ExportMode {
  return typeof (globalThis as unknown as { showSaveFilePicker?: SavePicker })
    .showSaveFilePicker === 'function'
    ? 'picker'
    : 'download-link';
}

/**
 * Write the ORIGINAL bytes to a user-chosen destination without any network
 * request and without re-encoding. With a save picker the file is streamed in
 * chunks; otherwise the file-backed OPFS Blob is handed to the browser, which
 * reads it from disk instead of memory. ARCH-013, DONE-011
 */
export async function exportOriginal(
  engine: Engine,
  id: string,
  name: string,
  total: number,
  onProgress?: (written: number) => void,
): Promise<void> {
  const picker = (globalThis as unknown as { showSaveFilePicker?: SavePicker }).showSaveFilePicker;
  if (picker) {
    // The picker needs the click's user activation, so open it before any other await.
    let handle: SaveHandle;
    try {
      handle = await picker({ suggestedName: name });
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') return; // the user cancelled
      throw new OneFlowError('EXPORT_FAILED');
    }
    const writable = await handle.createWritable();
    try {
      for (let off = 0; off < total; off += CHUNK) {
        const buf = await engine.readStorage(id, off, Math.min(CHUNK, total - off));
        await writable.write(buf);
        onProgress?.(Math.min(total, off + buf.byteLength));
      }
      await writable.close();
    } catch (e) {
      await writable.abort?.().catch(() => undefined);
      if (e instanceof DOMException && e.name === 'QuotaExceededError')
        throw new OneFlowError('QUOTA_EXCEEDED');
      throw new OneFlowError('EXPORT_FAILED');
    }
    return;
  }

  const file = await engine.getCompleteFile(id);
  const url = createObjectUrl(`export:${id}`, file);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Give the browser time to start reading before the URL is released.
  setTimeout(() => revokeObjectUrl(url), 60_000);
  onProgress?.(total);
}
